export interface CompanyInfoCompletionInput {
  companyName?: string;
  companyOrgNumber?: string;
  address1?: string;
  city?: string;
  postCode?: string;
  country?: string;
  invoiceEmail?: string;
  invoiceReference?: string;
}

export function isCompanyInfoComplete(
  input: CompanyInfoCompletionInput,
): boolean {
  const orgNumber = input.companyOrgNumber?.trim();
  const invoiceRef = input.invoiceReference?.trim();

  // Check required fields (peppol_id and address_line_2 are optional, not checked here)
  // Note: invoice_reference is required, but 'N/A' is a valid value indicating no reference needed
  const hasRequiredFields = Boolean(
    input.companyName?.trim() &&
    orgNumber &&
    orgNumber !== "TBD" &&
    input.invoiceEmail?.trim() &&
    input.address1?.trim() &&
    input.city?.trim() &&
    input.postCode?.trim() &&
    input.country?.trim() &&
    invoiceRef, // Must have a value, even if it's 'N/A'
  );

  // Check that required fields are not set to placeholder values
  // Note: 'N/A' is allowed for invoice_reference as it means no reference is needed
  const noPlaceholderValues =
    input.companyName?.trim().toUpperCase() !== "N/A" &&
    input.companyName?.trim().toUpperCase() !== "NA" &&
    orgNumber?.toUpperCase() !== "N/A" &&
    orgNumber?.toUpperCase() !== "NA" &&
    input.invoiceEmail?.trim().toUpperCase() !== "N/A" &&
    input.invoiceEmail?.trim().toUpperCase() !== "NA" &&
    input.address1?.trim().toUpperCase() !== "N/A" &&
    input.address1?.trim().toUpperCase() !== "NA" &&
    input.city?.trim().toUpperCase() !== "N/A" &&
    input.city?.trim().toUpperCase() !== "NA" &&
    input.postCode?.trim().toUpperCase() !== "N/A" &&
    input.postCode?.trim().toUpperCase() !== "NA" &&
    input.country?.trim().toUpperCase() !== "N/A" &&
    input.country?.trim().toUpperCase() !== "NA";
  // Removed check for invoice_reference !== 'N/A' since it's a valid value

  return hasRequiredFields && noPlaceholderValues;
}

/**
 * Formats an organization number the way the company's country writes it.
 * Only Swedish ones get a dash (XXXXXX-XXXX). A Danish CVR is 8 digits and
 * a Norwegian org number 9 digits; any other country just loses its dashes.
 */
export function formatOrgNumber(value: string, country?: string | null): string {
  switch (country) {
    case "SE": {
      const cleaned = value.replace(/[^a-zA-Z0-9]/g, "");
      if (cleaned.length <= 6) {
        return cleaned;
      }
      return cleaned.slice(0, 6) + "-" + cleaned.slice(6, 10);
    }
    case "DK":
      return value.replace(/\D/g, "").slice(0, 8);
    case "NO":
      return value.replace(/\D/g, "").slice(0, 9);
    default:
      return value.replace(/-/g, "");
  }
}

/** The company country an org number follows, from the venue's currency. */
export function orgNumberCountryFromCurrency(
  currency?: string | null,
): string | null {
  switch (currency) {
    case "SEK":
      return "SE";
    case "DKK":
      return "DK";
    case "NOK":
      return "NO";
    default:
      return null;
  }
}

/**
 * The country a venue's companies most likely belong to, for formatting
 * their org number and picking a new company's default country. A Danish
 * default language wins over the currency: some Danish venues bill in SEK,
 * and treating them as Swedish gave CVRs the Swedish dash (460381-50).
 */
export function venueCompanyCountry(
  currency?: string | null,
  defaultLanguage?: string | null,
): string | null {
  const lang = (defaultLanguage ?? "").toLowerCase();
  if (lang.startsWith("da")) return "DK";
  if (lang.startsWith("sv")) return "SE";
  if (lang.startsWith("no") || lang.startsWith("nb")) return "NO";
  return orgNumberCountryFromCurrency(currency);
}

/** An example org number for the company's country, for input placeholders. */
export function orgNumberExample(country?: string | null): string {
  switch (country) {
    case "SE":
      return "XXXXXX-XXXX";
    case "DK":
      return "12345678";
    case "NO":
      return "123456789";
    default:
      return "";
  }
}

/**
 * A Danish venue: DKK currency or a Danish default language (some Danish
 * venues bill in SEK). Its org number is the "CVR number", and its
 * companies' Peppol ID is their EAN number.
 *
 * Danish public institutions (police, municipalities, ministries) are
 * invoiced electronically to their EAN number: 13 digits, the same GLN that
 * serves as their Peppol ID. At a Danish venue - and only there - the
 * Peppol ID field is shown as "EAN number", right under the CVR, and holds
 * digits only.
 */
export function isDanishVenue(
  currency?: string | null,
  defaultLanguage?: string | null,
): boolean {
  return (
    currency === "DKK" || (defaultLanguage ?? "").toLowerCase().startsWith("da")
  );
}

/** The label for the Peppol ID field: "EAN number" at a Danish venue. */
export function peppolIdLabel(danishVenue: boolean): string {
  return danishVenue ? "EAN number" : "Peppol ID or GLN-number";
}

/** An EAN number as digits only at a Danish venue; elsewhere as typed. */
export function formatPeppolId(value: string, danishVenue: boolean): string {
  return danishVenue ? value.replace(/\D/g, "").slice(0, 13) : value;
}

/**
 * Why a Danish EAN number is wrong, or null when it is fine (or empty - it is
 * optional). Checks the length and the GS1 check digit, so a mistyped digit
 * is caught before invoices go to a number that doesn't exist.
 */
export function eanNumberError(value?: string | null): string | null {
  const ean = value?.trim() ?? "";
  if (!ean) return null;
  if (!/^\d{13}$/.test(ean)) return "An EAN number is 13 digits";
  const digits = ean.split("").map(Number);
  const sum = digits
    .slice(0, 12)
    .reduce((total, digit, i) => total + digit * (i % 2 === 0 ? 1 : 3), 0);
  if ((10 - (sum % 10)) % 10 !== digits[12]) {
    return "This EAN number is not valid - please check the digits";
  }
  return null;
}

/**
 * Whether a corporate booking's company profile must carry full invoicing
 * details (address, invoice email, reference, invoice type). Only bookings
 * settled by invoice need them; a company paying by card just needs its
 * name and organization number on file. An unknown payment method is
 * treated as invoice so nothing is skipped by accident.
 */
export function needsInvoiceDetails(paymentMethod?: string | null): boolean {
  return !paymentMethod || paymentMethod === "invoice";
}
